import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Download, Eye, Trash2, BookOpen, Printer, TestTube2, Upload, Edit, FileUp } from 'lucide-react';
import PublishModal from '@/components/PublishModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicationVaultPage() {
  const navigate = useNavigate();
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingPublication, setCheckingPublication] = useState(null);
  const [checkResults, setCheckResults] = useState(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishingPub, setPublishingPub] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testGeneratingPub, setTestGeneratingPub] = useState(null);
  const [showCourseProgress, setShowCourseProgress] = useState(false);
  const [courseProgress, setCourseProgress] = useState(0);
  const [courseProgressStep, setCourseProgressStep] = useState('');
  const [categories, setCategories] = useState([]);
  const [importing, setImporting] = useState(false);
  const [showPdfUploadModal, setShowPdfUploadModal] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfUploadProgress, setPdfUploadProgress] = useState(null);
  const [agreementMode, setAgreementMode] = useState('every_time');
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date-desc'); // date-desc, date-asc, title
  const [filterTier, setFilterTier] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');

  useEffect(() => {
    fetchPublications();
    fetchCategories();
    fetchAgreementMode();
  }, []);

  const fetchAgreementMode = async () => {
    try {
      const response = await axios.get(`${API}/publication-agreement/config`, { withCredentials: true });
      setAgreementMode(response.data.mode || 'every_time');
    } catch (error) {
      console.error('Error fetching agreement mode:', error);
    }
  };

  const updateAgreementMode = async (mode) => {
    const prev = agreementMode;
    setAgreementMode(mode);
    try {
      await axios.put(`${API}/admin/publication-agreement/settings`, { mode }, { withCredentials: true });
      toast.success(mode === 'once_per_user' ? 'Viewers will agree once.' : 'Viewers will agree every time.');
    } catch (error) {
      console.error('Error updating agreement mode:', error);
      toast.error('Failed to update agreement setting');
      setAgreementMode(prev);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/test-categories`);
      setCategories(response.data.filter(cat => cat.enabled));
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPublications = async () => {
    try {
      const response = await axios.get(`${API}/admin/generation/publications`, {
        withCredentials: true
      });
      setPublications(response.data.publications);
    } catch (error) {
      console.error('Error fetching publications:', error);
      toast.error('Failed to load publications');
    } finally {
      setLoading(false);
    }
  };
  
  // Filter and sort publications
  const getFilteredPublications = () => {
    let filtered = [...publications];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(pub => 
        pub.title?.toLowerCase().includes(query) ||
        pub.author?.toLowerCase().includes(query)
      );
    }
    
    // Apply tier filter
    if (filterTier !== 'all') {
      filtered = filtered.filter(pub => pub.tier === filterTier);
    }
    
    // Apply category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(pub => pub.category === filterCategory);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'date-asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        default:
          return 0;
      }
    });
    
    return filtered;
  };

  const handleView = (publicationId) => {
    // Navigate to publication view
    window.location.href = `/admin/publication-assembly?publication_id=${publicationId}`;
  };

  const handleImportDocx = async (e) => {
    const file = e.target.files[0];
    e.target.value = ''; // allow re-selecting same file
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error('Please select a .docx file');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setImporting(true);
    try {
      const response = await axios.post(`${API}/admin/publications/import-docx`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      const stats = response.data.stats || {};
      toast.success(
        `Imported "${response.data.publication?.title}" — ${stats.sections} sections, ${stats.tables} tables, ${stats.word_count} words. Saved as draft.`
      );
      await fetchPublications();
      // Open preview of the freshly imported draft
      if (response.data.id) {
        navigate(`/admin/publications/${response.data.id}/preview`);
      }
    } catch (error) {
      console.error('Error importing DOCX:', error);
      toast.error(error.response?.data?.detail || 'Failed to import document');
    } finally {
      setImporting(false);
    }
  };

  const handleUploadPdf = async ({ title, author, category, tier, file }) => {
    setPdfUploading(true);
    setPdfUploadProgress({ processed: 0, total: 0 });
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title);
      formData.append('author', author || 'Unknown');
      if (category) formData.append('category', category);
      if (tier) formData.append('tier', tier);

      const startRes = await axios.post(`${API}/admin/publications/upload-pdf/start`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      const { job_id, publication_id } = startRes.data;

      let finished = false;
      while (!finished) {
        await new Promise(r => setTimeout(r, 1500));
        const st = await axios.get(`${API}/admin/publications/upload-pdf/status/${job_id}`, { withCredentials: true });
        setPdfUploadProgress({ processed: st.data.processed_pages, total: st.data.total_pages });
        if (st.data.status === 'done') {
          finished = true;
          toast.success(`Uploaded ${st.data.total_pages}-page PDF as a new draft publication!`);
          setShowPdfUploadModal(false);
          await fetchPublications();
          navigate(`/admin/publications/${publication_id}/preview`);
        } else if (st.data.status === 'error') {
          finished = true;
          toast.error(st.data.error || 'PDF upload failed');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'PDF upload failed');
    } finally {
      setPdfUploading(false);
      setPdfUploadProgress(null);
    }
  };

  const handleDownload = async (publicationId, title) => {
    try {
      // Fetch the full publication data
      const response = await axios.get(
        `${API}/admin/generation/publications/${publicationId}`,
        { withCredentials: true }
      );
      
      const pub = response.data.publication;
      
      // Build text version
      let text = `${pub.title}\n`;
      text += `By ${pub.author}\n`;
      if (pub.affiliation) text += `${pub.affiliation}\n`;
      text += `\n${'='.repeat(60)}\n\n`;
      
      // Add chapters
      if (pub.chapters) {
        for (const chapter of pub.chapters) {
          text += `\n${chapter.title}\n`;
          text += `${'-'.repeat(chapter.title.length)}\n\n`;
          
          if (chapter.paragraphs) {
            for (const para of chapter.paragraphs) {
              text += `${para.content}\n\n`;
            }
          }
        }
      }
      
      // Copy to clipboard
      await navigator.clipboard.writeText(text);
      toast.success('Publication copied to clipboard! Paste it into your word processor.');
      
    } catch (error) {
      console.error('Error copying publication:', error);
      toast.error('Failed to copy publication');
    }
  };

  const handleDownloadDOCX = async (publicationId, title) => {
    try {
      const backendUrl = window.location.origin;
      const docxUrl = `${backendUrl}/api/admin/generation/publications/${publicationId}/export/docx`;
      
      // Use fetch with blob
      const response = await fetch(docxUrl, {
        method: 'GET',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to generate DOCX');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title.replace(/[^a-z0-9]/gi, '_')}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('DOCX downloaded!');
    } catch (error) {
      console.error('Error downloading DOCX:', error);
      toast.error('Failed to download DOCX');
    }
  };

  const handleFinalCheck = async (publicationId) => {
    try {
      setCheckingPublication(publicationId);
      const response = await axios.post(
        `${API}/admin/generation/publications/${publicationId}/final-check`,
        {},
        { withCredentials: true }
      );
      
      setCheckResults(response.data);
      toast.success('Final check complete!');
    } catch (error) {
      console.error('Error in final check:', error);
      toast.error('Failed to run final check');
    } finally {
      setCheckingPublication(null);
    }
  };

  const handlePublish = (publication) => {
    setPublishingPub(publication);
    setShowPublishModal(true);
  };

  const handlePublishSubmit = async (publishData) => {
    try {
      await axios.post(
        `${API}/admin/publications/${publishingPub.id}/publish`,
        publishData,
        { withCredentials: true }
      );
      toast.success('Publication published successfully!');
      setShowPublishModal(false);
      fetchPublications();
    } catch (error) {
      console.error('Error publishing:', error);
      toast.error(error.response?.data?.detail || 'Failed to publish');
    }
  };

  const handleGenerateTest = (publication) => {
    setTestGeneratingPub(publication);
    setShowTestModal(true);
  };

  const handleGenerateTestSubmit = async (testData) => {
    try {
      const response = await axios.post(
        `${API}/admin/publications/${testGeneratingPub.id}/generate-test`,
        testData,
        { withCredentials: true }
      );
      toast.success(`Test generated with ${response.data.question_count} questions!`);
      setShowTestModal(false);
    } catch (error) {
      console.error('Error generating test:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate test');
    }
  };

  const handleGenerateCourse = async (publicationId) => {
    if (!window.confirm('Generate a complete auto course from this publication using GPT-5? This will create a course with up to 3 chapters, flashcards, tests, and a final exam. This may take 2-3 minutes.')) {
      return;
    }

    try {
      // Show progress modal
      setShowCourseProgress(true);
      setCourseProgress(0);
      setCourseProgressStep('Initializing AI course generation...');
      
      // Simulate progress updates - GPT-5 is much faster
      const progressSteps = [
        { progress: 15, step: 'Analyzing publication content with GPT-5...', delay: 1000 },
        { progress: 30, step: 'Extracting 10-30 key terms and definitions...', delay: 2000 },
        { progress: 45, step: 'Creating up to 3 course chapters...', delay: 1500 },
        { progress: 60, step: 'Generating flashcards and study materials...', delay: 2000 },
        { progress: 75, step: 'Creating chapter tests (10 questions each)...', delay: 2000 },
        { progress: 90, step: 'Building final exam (30 questions, 30 mins)...', delay: 1500 },
        { progress: 95, step: 'Finalizing course structure...', delay: 500 }
      ];
      
      // Start the actual API call
      const apiPromise = axios.post(
        `${API}/admin/publications/${publicationId}/generate-course`,
        {},
        { withCredentials: true }
      );
      
      // Run progress simulation
      for (const step of progressSteps) {
        await new Promise(resolve => setTimeout(resolve, step.delay));
        setCourseProgress(step.progress);
        setCourseProgressStep(step.step);
      }
      
      // Wait for API to complete
      const response = await apiPromise;
      
      // Complete progress
      setCourseProgress(100);
      setCourseProgressStep('Course generated successfully!');
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setShowCourseProgress(false);
      toast.success(`Course "${response.data.title}" generated with ${response.data.chapters} chapters!`);
      
    } catch (error) {
      console.error('Error generating course:', error);
      setShowCourseProgress(false);
      toast.error(error.response?.data?.detail || 'Failed to generate course');
    }
  };

  const handlePrint = async (publicationId) => {
    try {
      const pdfUrl = `${API}/admin/generation/publications/${publicationId}/export/pdf`;
      window.open(pdfUrl, '_blank');
      toast.success('Opening print preview...');
    } catch (error) {
      console.error('Error printing:', error);
      toast.error('Failed to open print preview');
    }
  };

  const handleDelete = async (publicationId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/publications/${publicationId}`, { withCredentials: true });
      toast.success('Publication deleted successfully!');
      fetchPublications();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete publication');
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-[#F4E4C1] to-[#E8D5B7] py-16">
        <div className="max-w-6xl mx-auto px-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => window.location.href = '/admin/generation'}
              className="retro-button flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Generation
            </button>
            
            <h1 className="text-4xl retro-title text-[#6B4423]">Publication Vault</h1>
            
            <div className="flex items-center gap-2">
              <input
                id="import-docx-input"
                type="file"
                accept=".docx"
                onChange={handleImportDocx}
                disabled={importing}
                className="hidden"
                data-testid="import-docx-input"
              />
              <button
                onClick={() => document.getElementById('import-docx-input').click()}
                disabled={importing}
                data-testid="import-docx-button"
                className="retro-button flex items-center gap-2 bg-[#1B4B5A] hover:bg-[#143a45] text-white disabled:opacity-60"
                title="Import a Claude-authored .docx publication"
              >
                <FileUp className="w-4 h-4" />
                {importing ? 'Importing...' : 'Import DOCX'}
              </button>
              <button
                onClick={() => setShowPdfUploadModal(true)}
                data-testid="upload-pdf-button"
                className="retro-button flex items-center gap-2 bg-[#B5813B] hover:bg-[#9c6f31] text-white"
                title="Upload a finished PDF as a publication — copied page-for-page, no AI rewriting"
              >
                <Upload className="w-4 h-4" />
                Upload PDF
              </button>
            </div>
          </div>

          {/* Viewing Agreement Setting */}
          <div className="admin-card mb-6" data-testid="agreement-settings-panel">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-white">Viewing Agreement</h3>
                <p className="text-sm text-white/80">
                  Choose how often readers must accept the copyright agreement before viewing a publication.
                </p>
              </div>
              <select
                data-testid="agreement-mode-select"
                value={agreementMode}
                onChange={(e) => updateAgreementMode(e.target.value)}
                className="px-4 py-2 border-2 border-[#8B6F47] rounded focus:outline-none focus:border-[#6B4423] text-gray-900 bg-white font-semibold"
              >
                <option value="every_time">Agree every time</option>
                <option value="once_per_user">Agree once per user</option>
              </select>
            </div>
          </div>

          {/* Publications List */}
          <div className="admin-card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                <span data-testid="publications-count">Your Publications ({getFilteredPublications().length})</span>
              </h2>
            </div>
            
            {/* Search and Filter Controls */}
            <div className="mb-6 space-y-4">
              {/* Search Bar */}
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by title or author..."
                  className="w-full px-4 py-3 pl-10 border-2 border-[#8B6F47] rounded focus:outline-none focus:border-[#6B4423] text-gray-900"
                />
                <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
              
              {/* Filter Controls */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Sort By */}
                <div>
                  <label className="block text-white font-bold mb-2 text-sm">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-[#8B6F47] rounded focus:outline-none focus:border-[#6B4423] text-gray-900"
                  >
                    <option value="date-desc">Newest First</option>
                    <option value="date-asc">Oldest First</option>
                    <option value="title">Title (A-Z)</option>
                  </select>
                </div>
                
                {/* Filter by Tier */}
                <div>
                  <label className="block text-white font-bold mb-2 text-sm">Filter by Tier</label>
                  <select
                    value={filterTier}
                    onChange={(e) => setFilterTier(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-[#8B6F47] rounded focus:outline-none focus:border-[#6B4423] text-gray-900"
                  >
                    <option value="all">All Tiers</option>
                    <option value="basic">Basic</option>
                    <option value="comps_ready">Comps Ready</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
                
                {/* Filter by Category */}
                <div>
                  <label className="block text-white font-bold mb-2 text-sm">Filter by Category</label>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-[#8B6F47] rounded focus:outline-none focus:border-[#6B4423] text-gray-900"
                  >
                    <option value="all">All Categories</option>
                    {categories.map((cat, idx) => (
                      <option key={`cat-filter-${idx}-${cat.slug}`} value={cat.slug}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Active Filters Display */}
              {(searchQuery || filterTier !== 'all' || filterCategory !== 'all') && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-white font-bold">Active Filters:</span>
                  {searchQuery && (
                    <span className="px-3 py-1 bg-[#E8934D] text-white rounded-full">
                      Search: "{searchQuery}"
                    </span>
                  )}
                  {filterTier !== 'all' && (
                    <span className="px-3 py-1 bg-[#4D9E7C] text-white rounded-full">
                      Tier: {filterTier}
                    </span>
                  )}
                  {filterCategory !== 'all' && (
                    <span className="px-3 py-1 bg-[#E85D4D] text-white rounded-full">
                      Category: {categories.find(c => c.slug === filterCategory)?.name}
                    </span>
                  )}
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setFilterTier('all');
                      setFilterCategory('all');
                    }}
                    className="text-white hover:text-[#E85D4D] underline ml-2"
                  >
                    Clear All
                  </button>
                </div>
              )}
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="text-xl text-white">Loading publications...</div>
              </div>
            ) : getFilteredPublications().length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-xl text-gray-600 mb-4">
                  {publications.length === 0 ? 'No publications yet' : 'No publications match your filters'}
                </p>
                <p className="text-gray-500 mb-6">
                  {publications.length === 0 
                    ? 'Create your first publication in the Generation page'
                    : 'Try adjusting your search or filters'
                  }
                </p>
                {publications.length === 0 && (
                  <button
                    onClick={() => window.location.href = '/admin/generation'}
                    className="retro-button"
                  >
                    Go to Generation
                  </button>
                )}
              </div>
            ) : (
              <div className="grid gap-4">
                {getFilteredPublications().map((pub) => (
                  <div
                    key={pub.id}
                    data-testid={`publication-card-${pub.id}`}
                    className="p-6 bg-white border-2 border-[#8B6F47] rounded hover:border-[#6B4423] transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-[#6B4423] mb-2">
                          {pub.title}
                        </h3>
                        <p className="text-lg text-gray-700 mb-2">
                          <strong>Author:</strong> {pub.author}
                        </p>
                        {pub.affiliation && (
                          <p className="text-gray-600 mb-2">{pub.affiliation}</p>
                        )}
                        
                        <div className="flex gap-4 mt-3 text-sm text-gray-700">
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            {pub.source_format === 'docx_import'
                              ? `${pub.sections?.length || 0} section(s)`
                              : `${pub.chapter_ids?.length || 0} chapter(s)`}
                          </span>
                          {pub.source_format === 'docx_import' && (
                            <span className="px-2 py-0.5 rounded-full bg-[#FDF6EC] text-[#B5813B] border border-[#B5813B] text-xs font-semibold">
                              Imported
                            </span>
                          )}
                          <span>Style: {pub.citation_style}</span>
                          <span>Tone: {pub.tone}</span>
                          <span className={`font-semibold ${pub.status === 'published' ? 'text-green-600' : 'text-gray-600'}`}>
                            Status: {pub.status}
                          </span>
                        </div>
                        
                        <div className="mt-3 text-xs text-gray-500">
                          Created: {formatDate(pub.created_at)}
                          {pub.updated_at && pub.updated_at !== pub.created_at && (
                            <> • Updated: {formatDate(pub.updated_at)}</>
                          )}
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2 ml-4 flex-wrap">
                        <button
                          onClick={() => navigate(`/admin/publications/${pub.id}/preview`)}
                          data-testid={`preview-publication-${pub.id}`}
                          className="retro-button flex items-center gap-2 text-sm bg-[#1B4B5A] hover:bg-[#143a45] text-white"
                          title="Preview (design view)"
                        >
                          <Eye className="w-4 h-4" />
                          Preview
                        </button>
                        <button
                          onClick={() => navigate(
                            (pub.sections?.length > 0 || pub.source_format === 'pdf_upload')
                              ? `/admin/publications/${pub.id}/preview`
                              : `/admin/publication-assembly?publicationId=${pub.id}`
                          )}
                          data-testid={`edit-publication-${pub.id}`}
                          className="retro-button flex items-center gap-2 text-sm bg-blue-600 hover:bg-blue-700"
                          title="Edit Publication"
                        >
                          <Edit className="w-4 h-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => handleFinalCheck(pub.id)}
                          data-testid={`final-check-publication-${pub.id}`}
                          disabled={checkingPublication === pub.id}
                          className="retro-button flex items-center gap-2 text-sm"
                          title="Final Check - Validates formatting and structure"
                        >
                          {checkingPublication === pub.id ? 'Checking...' : 'Final Check'}
                        </button>
                        <button
                          onClick={() => handlePublish(pub)}
                          data-testid={`publish-publication-${pub.id}`}
                          className="retro-button flex items-center gap-2 text-sm bg-green-600 hover:bg-green-700"
                          title="Publish Document"
                        >
                          <Upload className="w-4 h-4" />
                          Publish
                        </button>
                        <button
                          onClick={() => handleGenerateCourse(pub.id)}
                          data-testid={`auto-course-publication-${pub.id}`}
                          className="retro-button flex items-center gap-2 text-sm bg-indigo-600 hover:bg-indigo-700"
                          title="Auto Generate Course"
                        >
                          <BookOpen className="w-4 h-4" />
                          Auto Course
                        </button>
                        <button
                          onClick={() => handleGenerateTest(pub)}
                          data-testid={`generate-test-publication-${pub.id}`}
                          className="retro-button flex items-center gap-2 text-sm bg-purple-600 hover:bg-purple-700"
                          title="Generate Test"
                        >
                          <TestTube2 className="w-4 h-4" />
                          Test
                        </button>
                        <button
                          onClick={() => handlePrint(pub.id)}
                          data-testid={`print-publication-${pub.id}`}
                          className="retro-button flex items-center gap-2 text-sm bg-gray-600 hover:bg-gray-700"
                          title="Print"
                        >
                          <Printer className="w-4 h-4" />
                          Print
                        </button>
                        <button
                          onClick={() => handleView(pub.id)}
                          data-testid={`view-publication-${pub.id}`}
                          className="retro-button flex items-center gap-2 text-sm"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadDOCX(pub.id, pub.title)}
                          data-testid={`docx-publication-${pub.id}`}
                          className="retro-button flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-sm"
                          title="Download DOCX"
                        >
                          <Download className="w-4 h-4" />
                          DOCX
                        </button>
                        <button
                          onClick={() => handleDownload(pub.id, pub.title)}
                          data-testid={`copy-publication-${pub.id}`}
                          className="retro-button flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-sm"
                          title="Copy to Clipboard"
                        >
                          <Download className="w-4 h-4" />
                          Copy
                        </button>
                        <button
                          onClick={() => handleDelete(pub.id, pub.title)}
                          data-testid={`delete-publication-${pub.id}`}
                          className="retro-button flex items-center gap-2 bg-red-600 hover:bg-red-700 text-sm"
                          title="Delete Publication"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Final Check Results */}
            {checkResults && (
              <div className="mt-6 p-6 bg-white border-2 border-[#8B6F47] rounded">
                <h3 className="text-2xl font-bold text-white mb-4">Final Check Results</h3>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-3 bg-[#F4E4C1] rounded">
                    <div className="text-sm text-gray-600">Word Count</div>
                    <div className="text-2xl font-bold text-[#6B4423]">{checkResults.statistics.word_count}</div>
                  </div>
                  <div className="p-3 bg-[#F4E4C1] rounded">
                    <div className="text-sm text-gray-600">Paragraphs</div>
                    <div className="text-2xl font-bold text-[#6B4423]">{checkResults.statistics.paragraph_count}</div>
                  </div>
                  <div className="p-3 bg-[#F4E4C1] rounded">
                    <div className="text-sm text-gray-600">Chapters</div>
                    <div className="text-2xl font-bold text-[#6B4423]">{checkResults.statistics.chapter_count}</div>
                  </div>
                  <div className="p-3 bg-[#F4E4C1] rounded">
                    <div className="text-sm text-gray-600">Avg Words/Para</div>
                    <div className="text-2xl font-bold text-[#6B4423]">{checkResults.statistics.avg_words_per_paragraph}</div>
                  </div>
                </div>
                
                {checkResults.issues.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-bold text-red-600 mb-2">Issues Found:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {checkResults.issues.map((issue, idx) => (
                        <li key={idx} className="text-red-700">{issue}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {checkResults.suggestions.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-bold text-yellow-600 mb-2">Suggestions:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {checkResults.suggestions.map((suggestion, idx) => (
                        <li key={idx} className="text-yellow-700">{suggestion}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="mt-4">
                  <span className={`inline-block px-4 py-2 rounded font-bold ${
                    checkResults.status === 'ready' ? 'bg-green-200 text-green-800' : 'bg-yellow-200 text-yellow-800'
                  }`}>
                    Status: {checkResults.status === 'ready' ? 'Ready to Publish' : 'Needs Review'}
                  </span>
                </div>
                
                <button
                  onClick={() => setCheckResults(null)}
                  className="mt-4 retro-button"
                >
                  Close
                </button>
              </div>
            )}

            {/* Placeholder for Phase 6 features */}
            {publications.length > 0 && (
              <div className="mt-6 p-4 bg-[#F4E4C1] rounded border-2 border-[#8B6F47]">
                <p className="text-[#6B4423]">
                  <strong>Coming in Phase 6:</strong> Export to PDF/DOCX/ePub, versioning, encryption, search/filters
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Publish Modal */}
      {showPublishModal && (
        <PublishModal
          publication={publishingPub}
          categories={categories}
          onClose={() => setShowPublishModal(false)}
          onSubmit={handlePublishSubmit}
        />
      )}

      {/* Generate Test Modal */}
      {showTestModal && (
        <GenerateTestModal
          publication={testGeneratingPub}
          categories={categories}
          onClose={() => setShowTestModal(false)}
          onSubmit={handleGenerateTestSubmit}
        />
      )}

      {/* Upload PDF Modal */}
      {showPdfUploadModal && (
        <UploadPdfModal
          categories={categories}
          uploading={pdfUploading}
          progress={pdfUploadProgress}
          onClose={() => setShowPdfUploadModal(false)}
          onSubmit={handleUploadPdf}
        />
      )}

      {/* Course Generation Progress Modal */}
      {showCourseProgress && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full">
            <h2 className="text-3xl font-bold text-[#6B4423] mb-6 text-center">
              🤖 AI Course Generation in Progress
            </h2>
            
            {/* Progress Bar */}
            <div className="mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-sm font-semibold text-[#6B4423]">{courseProgressStep}</span>
                <span className="text-sm font-bold text-[#6B4423]">{courseProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 h-6 rounded-full transition-all duration-500 ease-out flex items-center justify-center"
                  style={{ width: `${courseProgress}%` }}
                >
                  {courseProgress > 10 && (
                    <span className="text-white text-xs font-bold">{courseProgress}%</span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Progress Details */}
            <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-4">
              <h3 className="font-bold text-blue-800 mb-2">What's being generated:</h3>
              <ul className="space-y-1 text-sm text-blue-700">
                <li className={courseProgress >= 30 ? 'line-through opacity-50' : ''}>✓ Extracting 10-30 key learning terms</li>
                <li className={courseProgress >= 45 ? 'line-through opacity-50' : courseProgress >= 30 ? 'font-bold' : ''}>✓ Creating up to 3 course chapters (10 terms each)</li>
                <li className={courseProgress >= 60 ? 'line-through opacity-50' : courseProgress >= 45 ? 'font-bold' : ''}>✓ Generating flashcards for each chapter</li>
                <li className={courseProgress >= 75 ? 'line-through opacity-50' : courseProgress >= 60 ? 'font-bold' : ''}>✓ Creating 10-question chapter tests</li>
                <li className={courseProgress >= 90 ? 'line-through opacity-50' : courseProgress >= 75 ? 'font-bold' : ''}>✓ Building 30-question final exam</li>
                <li className={courseProgress >= 100 ? 'font-bold text-green-600' : ''}>✓ Finalizing course</li>
              </ul>
            </div>
            
            {/* Animated loader */}
            {courseProgress < 100 && (
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-indigo-600"></div>
              </div>
            )}
            
            {courseProgress === 100 && (
              <div className="text-center">
                <div className="text-6xl mb-2">🎉</div>
                <p className="text-lg font-bold text-green-600">Course Generated Successfully!</p>
              </div>
            )}
            
            <p className="text-center text-sm text-gray-600 mt-4">
              Please wait... GPT-5 AI is generating comprehensive course materials. This typically takes 2-3 minutes.
            </p>
          </div>
        </div>
      )}
    </Layout>
  );
}

// Generate Test Modal Component
function GenerateTestModal({ publication, categories, onClose, onSubmit }) {
  const [testTitle, setTestTitle] = useState(`Test: ${publication.title}`);
  const [tier, setTier] = useState('basic');
  const [category, setCategory] = useState('');
  const [timeLimit, setTimeLimit] = useState('');
  const [passingScore, setPassingScore] = useState('70');
  const [generating, setGenerating] = useState(false);

  // Filter categories by selected tier
  const filteredCategories = categories.filter(cat => cat.tier === tier);

  // Reset category when tier changes
  const handleTierChange = (newTier) => {
    setTier(newTier);
    setCategory(''); // Reset category selection when tier changes
  };

  const handleSubmit = async () => {
    if (!category) {
      alert('Please select a category');
      return;
    }

    setGenerating(true);
    await onSubmit({
      title: testTitle,
      tier,
      category,
      time_limit_seconds: timeLimit ? parseInt(timeLimit) * 60 : null,
      passing_score: parseInt(passingScore)
    });
    setGenerating(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full">
        <h2 className="text-3xl font-bold text-[#6B4423] mb-6">Generate Test from Publication</h2>
        <p className="text-gray-700 mb-6">AI will analyze the publication content and create test questions.</p>
        
        <div className="space-y-4">
          {/* Test Title */}
          <div>
            <label className="block font-bold text-[#6B4423] mb-2">Test Title *</label>
            <input
              type="text"
              value={testTitle}
              onChange={(e) => setTestTitle(e.target.value)}
              className="w-full border-2 border-gray-300 rounded p-2 text-gray-800"
            />
          </div>

          {/* Tier */}
          <div>
            <label className="block font-bold text-[#6B4423] mb-2">Tier * (select tier first)</label>
            <select
              value={tier}
              onChange={(e) => handleTierChange(e.target.value)}
              className="w-full border-2 border-gray-300 rounded p-2 text-gray-800"
            >
              <option value="basic">Basic</option>
              <option value="comps_ready">Comps Ready</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          {/* Category - Filtered by Tier */}
          <div>
            <label className="block font-bold text-[#6B4423] mb-2">
              Category * (showing {tier === 'basic' ? 'Basic' : tier === 'comps_ready' ? "Comps Ready" : 'Advanced'} categories)
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full border-2 border-gray-300 rounded p-2 text-gray-800"
            >
              <option value="">Select category...</option>
              {filteredCategories.map((cat, idx) => (
                <option key={`pub-cat-${idx}-${cat.slug}`} value={cat.slug}>{cat.icon} {cat.name}</option>
              ))}
            </select>
            {filteredCategories.length === 0 && (
              <p className="text-sm text-red-600 mt-1">No categories available for this tier. Please add categories in Test Categories.</p>
            )}
          </div>

          {/* Time Limit */}
          <div>
            <label className="block font-bold text-[#6B4423] mb-2">Time Limit (minutes, optional)</label>
            <input
              type="number"
              value={timeLimit}
              onChange={(e) => setTimeLimit(e.target.value)}
              className="w-full border-2 border-gray-300 rounded p-2 text-gray-800"
              placeholder="e.g., 60"
            />
          </div>

          {/* Passing Score */}
          <div>
            <label className="block font-bold text-[#6B4423] mb-2">Passing Score (%)</label>
            <input
              type="number"
              value={passingScore}
              onChange={(e) => setPassingScore(e.target.value)}
              className="w-full border-2 border-gray-300 rounded p-2 text-gray-800"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4 mt-6">
          <button
            onClick={handleSubmit}
            disabled={generating}
            className="flex-1 bg-purple-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-purple-700 disabled:opacity-50"
          >
            {generating ? 'Generating...' : 'Generate Test'}
          </button>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-300 text-gray-800 rounded-lg font-bold hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Upload PDF Modal Component — verbatim page-image publication, zero AI cost
function UploadPdfModal({ categories, uploading, progress, onClose, onSubmit }) {
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [tier, setTier] = useState('basic');
  const [category, setCategory] = useState('');
  const [file, setFile] = useState(null);

  const filteredCategories = categories.filter(cat => cat.tier === tier);

  const handleSubmit = () => {
    if (!title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!file) {
      toast.error('Please select a PDF file');
      return;
    }
    onSubmit({ title, author, category, tier, file });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full">
        <h2 className="text-3xl font-bold text-[#6B4423] mb-2">Upload PDF Publication</h2>
        <p className="text-gray-700 mb-6">Your PDF is copied page-for-page (charts, layout, and design preserved exactly) — no AI rewriting, no extra credits used here.</p>

        <div className="space-y-4">
          <div>
            <label className="block font-bold text-[#6B4423] mb-2">Title *</label>
            <input
              type="text"
              data-testid="pdf-upload-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={uploading}
              className="w-full border-2 border-gray-300 rounded p-2 text-gray-800"
              placeholder="e.g., The Complete Guide to Meridian Theory"
            />
          </div>

          <div>
            <label className="block font-bold text-[#6B4423] mb-2">Author</label>
            <input
              type="text"
              data-testid="pdf-upload-author-input"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              disabled={uploading}
              className="w-full border-2 border-gray-300 rounded p-2 text-gray-800"
              placeholder="e.g., Ernie"
            />
          </div>

          <div>
            <label className="block font-bold text-[#6B4423] mb-2">Tier</label>
            <select
              data-testid="pdf-upload-tier-select"
              value={tier}
              onChange={(e) => { setTier(e.target.value); setCategory(''); }}
              disabled={uploading}
              className="w-full border-2 border-gray-300 rounded p-2 text-gray-800"
            >
              <option value="basic">Basic</option>
              <option value="comps_ready">Comps Ready</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          <div>
            <label className="block font-bold text-[#6B4423] mb-2">Category</label>
            <select
              data-testid="pdf-upload-category-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={uploading}
              className="w-full border-2 border-gray-300 rounded p-2 text-gray-800"
            >
              <option value="">Select category...</option>
              {filteredCategories.map((cat, idx) => (
                <option key={`pdf-upload-cat-${idx}-${cat.slug}`} value={cat.slug}>{cat.icon} {cat.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-bold text-[#6B4423] mb-2">PDF File *</label>
            <input
              type="file"
              accept=".pdf"
              data-testid="pdf-upload-file-input"
              onChange={(e) => setFile(e.target.files[0] || null)}
              disabled={uploading}
              className="w-full border-2 border-gray-300 rounded p-2 text-gray-800"
            />
          </div>

          {uploading && progress && (
            <p className="text-sm text-[#B5813B] font-bold" data-testid="pdf-upload-progress">
              Rendering pages... {progress.total ? `${progress.processed} / ${progress.total}` : 'starting'}
            </p>
          )}
        </div>

        <div className="flex gap-4 mt-6">
          <button
            onClick={handleSubmit}
            disabled={uploading}
            data-testid="pdf-upload-submit-button"
            className="flex-1 bg-[#B5813B] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#9c6f31] disabled:opacity-50"
          >
            {uploading ? 'Uploading...' : 'Upload & Create Draft'}
          </button>
          <button
            onClick={onClose}
            disabled={uploading}
            data-testid="pdf-upload-cancel-button"
            className="px-6 py-3 bg-gray-300 text-gray-800 rounded-lg font-bold hover:bg-gray-400 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
